import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

export interface PatientDocument {
  id: string;
  fileName: string;
  createdAt: string;
}

export interface PatientDocumentsListRequest {
  limit: number;
  page: number;
}

export interface PatientDocumentsListResponse {
  data: PatientDocument[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface GenerateUploadUrlRequest {
  fileName: string;
}

export interface GenerateUploadUrlResponse {
  uploadUrl: string;
  id: string;
}

export interface ConfirmUploadResponse {
  id: string;
  status: string;
}

export interface GenerateDownloadUrlResponse {
  downloadUrl: string;
}

export const patientDocumentsApiSlice = createApi({
  reducerPath: 'patientDocumentsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.PatientDocuments],
  endpoints: (builder) => ({
    getPatientDocuments: builder.query<
      PatientDocumentsListResponse,
      { patientId: string; params: PatientDocumentsListRequest }
    >({
      query: ({ patientId, params }) => ({
        url: `/patient-documents/${patientId}`,
        method: 'POST',
        body: params
      }),
      providesTags: (result, _, { patientId }) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: API_SLICE.PatientDocuments as const, id })),
              { type: API_SLICE.PatientDocuments as const, id: `LIST_${patientId}` }
            ]
          : [{ type: API_SLICE.PatientDocuments as const, id: `LIST_${patientId}` }]
    }),

    generateUploadUrl: builder.mutation<
      GenerateUploadUrlResponse,
      { patientId: string; fileName: string }
    >({
      query: ({ patientId, fileName }) => ({
        url: `/patient-documents/${patientId}/upload-url`,
        method: 'POST',
        body: { fileName }
      })
    }),
    confirmUpload: builder.mutation<ConfirmUploadResponse, string>({
      query: (id) => ({
        url: `/patient-documents/${id}/confirm-upload`,
        method: 'POST'
      }),
      invalidatesTags: () => [{ type: API_SLICE.PatientDocuments as const, id: 'LIST' }]
    }),

    generateDownloadUrl: builder.query<GenerateDownloadUrlResponse, string>({
      query: (fileId) => ({
        url: `/patient-documents/${fileId}/download-url`,
        method: 'GET'
      })
    }),

    deletePatientDocument: builder.mutation<void, { patientId: string; fileId: string }>({
      query: ({ fileId }) => ({
        url: `/patient-documents/${fileId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { patientId, fileId }) => [
        { type: API_SLICE.PatientDocuments as const, id: fileId },
        { type: API_SLICE.PatientDocuments as const, id: `LIST_${patientId}` }
      ]
    })
  })
});

export const {
  useGetPatientDocumentsQuery,
  useGenerateUploadUrlMutation,
  useConfirmUploadMutation,
  useLazyGenerateDownloadUrlQuery,
  useDeletePatientDocumentMutation
} = patientDocumentsApiSlice;
